/* Production NotificationBell — surfaces 4 kinds of items the tech
 * should action: new assignments, rework returns, flag-acked unblocks,
 * and STAT/Rush due-date alerts.
 *
 * Reads /api/production/notifications every 60s. Grouped by kind so the
 * tech sees "8 new cases" instead of 8 individual rows. Each group
 * collapses by default; click to expand and drill into a case.
 *
 * 1-hour snooze persisted in localStorage — silences the badge without
 * hiding the data.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Bell, BellSlash, Package, ArrowUUpLeft, CheckCircle, Lightning,
    Fire, ArrowRight, X, CaretDown, CaretUp,
} from "@phosphor-icons/react";

const SNOOZE_MS = 60 * 60 * 1000;
const SNOOZE_KEY = "production.notif.snoozed_until";

function getSnoozedUntil() {
    try {
        const v = Number(window.localStorage.getItem(SNOOZE_KEY));
        return v && v > Date.now() ? v : 0;
    } catch { return 0; }
}

/* Kind taxonomy — drives chip color + group icon + drill-into target. */
const KIND_META = {
    new_assignment: {
        label: "New cases to acknowledge",
        Icon: Package,
        chipClass: "bg-violet-100 text-violet-700",
        target: "/coe/production/queue",
    },
    rework: {
        label: "Sent back for rework",
        Icon: ArrowUUpLeft,
        chipClass: "bg-amber-100 text-amber-800",
        target: "/coe/production/queue",
    },
    flag_acked: {
        label: "Your flags were cleared",
        Icon: CheckCircle,
        chipClass: "bg-emerald-100 text-emerald-700",
        target: "/coe/production/queue",
    },
    due_date_alert: {
        label: "Past SLA — STAT/Rush",
        Icon: Fire,
        chipClass: "bg-rose-100 text-rose-700",
        target: "/coe/production/queue?priority=rush",
    },
};

const PRIORITY_BADGE = {
    stat: { label: "STAT", cls: "bg-rose-600 text-white" },
    rush: { label: "Rush", cls: "bg-amber-500 text-white" },
};


export default function ProductionNotificationBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(true);
    const [snoozedUntil, setSnoozedUntil] = useState(getSnoozedUntil());
    const [expanded, setExpanded] = useState({});
    const ref = useRef(null);
    const navigate = useNavigate();

    const reload = () => {
        api.get("/production/notifications")
            .then((r) => setData(r.data || { total: 0, items: [] }))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
        const t = setInterval(reload, 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) {
            document.addEventListener("mousedown", onClick);
            return () => document.removeEventListener("mousedown", onClick);
        }
    }, [open]);

    const liveCount = data.total || 0;
    const snoozedNow = snoozedUntil > Date.now();
    const visibleCount = snoozedNow ? 0 : liveCount;

    /* Group items by kind so the bell stays readable when there are 20+. */
    const groups = useMemo(() => {
        const g = new Map();
        for (const it of data.items || []) {
            if (!g.has(it.kind)) g.set(it.kind, []);
            g.get(it.kind).push(it);
        }
        return Array.from(g.entries()).map(([kind, items]) => ({ kind, items }));
    }, [data]);

    const markAllRead = () => {
        const until = Date.now() + SNOOZE_MS;
        try { window.localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* */ }
        setSnoozedUntil(until);
    };

    const unsnooze = () => {
        try { window.localStorage.removeItem(SNOOZE_KEY); } catch { /* */ }
        setSnoozedUntil(0);
    };

    const drillInto = (item) => {
        setOpen(false);
        navigate(`/coe/production/case/${item.case_id}`);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="relative w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors"
                aria-label={`Notifications, ${visibleCount} item${visibleCount === 1 ? "" : "s"}`}
                data-testid="production-notification-bell"
            >
                <Bell size={18} weight={visibleCount > 0 ? "fill" : "regular"} className={visibleCount > 0 ? "text-primary" : "text-muted-foreground"} />
                {visibleCount > 0 && (
                    <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold tabular-nums grid place-items-center border-2 border-card"
                        data-testid="production-notification-bell-badge"
                    >
                        {visibleCount > 99 ? "99+" : visibleCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[380px] bg-card border border-border rounded-2xl shadow-xl overflow-hidden z-50"
                    data-testid="production-notification-panel"
                >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-border flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <div className="font-display font-semibold text-sm">Action needed</div>
                            <div className="text-[11px] text-muted-foreground">
                                {liveCount === 0
                                    ? "Nothing waiting on you"
                                    : snoozedNow
                                        ? `${liveCount} item${liveCount === 1 ? "" : "s"} · snoozed`
                                        : `${liveCount} item${liveCount === 1 ? "" : "s"} on your plate`}
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {liveCount > 0 && !snoozedNow && (
                                <button
                                    type="button"
                                    onClick={markAllRead}
                                    className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid="production-notification-mark-all-read"
                                    title="Silences the badge for 1 hour. Items stay live in your queue."
                                >
                                    <BellSlash size={11} weight="bold" />
                                    Mark all read
                                </button>
                            )}
                            {snoozedNow && (
                                <button
                                    type="button"
                                    onClick={unsnooze}
                                    className="text-[11px] font-semibold text-primary hover:underline"
                                    data-testid="production-notification-unsnooze"
                                >
                                    Un-snooze
                                </button>
                            )}
                            <button
                                type="button" onClick={() => setOpen(false)}
                                className="text-muted-foreground hover:text-foreground"
                                aria-label="Close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    {loading && (
                        <div className="px-4 py-6 text-sm text-muted-foreground text-center">Loading…</div>
                    )}

                    {!loading && groups.length === 0 && (
                        <div className="px-4 py-8 text-center" data-testid="production-notification-empty">
                            <CheckCircle size={28} weight="fill" className="mx-auto text-emerald-500 mb-2" />
                            <div className="text-sm font-semibold">You're all caught up</div>
                            <div className="text-[11px] text-muted-foreground mt-1">New assignments, rework, and SLA alerts will appear here.</div>
                        </div>
                    )}

                    {!loading && groups.length > 0 && (
                        <div className="max-h-[440px] overflow-y-auto" data-testid="production-notification-groups">
                            {groups.map((g) => {
                                const meta = KIND_META[g.kind];
                                if (!meta) return null;
                                const isOpen = expanded[g.kind] || false;
                                return (
                                    <div key={g.kind} className="border-b border-border last:border-b-0" data-testid={`production-notification-group-${g.kind}`}>
                                        <button
                                            type="button"
                                            onClick={() => setExpanded((e) => ({ ...e, [g.kind]: !isOpen }))}
                                            className="w-full px-4 py-3 hover:bg-secondary/40 text-left flex items-center gap-3"
                                        >
                                            <div className={`w-8 h-8 rounded-lg ${meta.chipClass} grid place-items-center shrink-0`}>
                                                <meta.Icon size={14} weight="fill" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold truncate">{meta.label}</div>
                                                <div className="text-[11px] text-muted-foreground">
                                                    {g.items.length} item{g.items.length === 1 ? "" : "s"}
                                                </div>
                                            </div>
                                            {isOpen ? <CaretUp size={12} className="text-muted-foreground" /> : <CaretDown size={12} className="text-muted-foreground" />}
                                        </button>
                                        {isOpen && (
                                            <div className="bg-secondary/30 border-t border-border">
                                                {g.items.slice(0, 12).map((it) => {
                                                    const pBadge = PRIORITY_BADGE[it.priority];
                                                    return (
                                                        <button
                                                            key={it.id}
                                                            type="button"
                                                            onClick={() => drillInto(it)}
                                                            className="w-full text-left px-4 py-2.5 hover:bg-card flex items-start gap-2.5 border-b border-border/50 last:border-b-0"
                                                            data-testid={`production-notif-row-${it.id}`}
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    <span className="text-xs font-semibold truncate">{it.patient_name}</span>
                                                                    {it.case_code && (
                                                                        <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-card text-muted-foreground border border-border">{it.case_code}</span>
                                                                    )}
                                                                    {pBadge && (
                                                                        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${pBadge.cls}`}>
                                                                            {pBadge.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{it.message}</div>
                                                            </div>
                                                            <ArrowRight size={12} className="text-muted-foreground shrink-0 mt-1" />
                                                        </button>
                                                    );
                                                })}
                                                {g.items.length > 12 && (
                                                    <div className="px-4 py-2 text-[10px] text-center text-muted-foreground">
                                                        +{g.items.length - 12} more — open the queue to see all
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="border-t border-border px-4 py-2 bg-secondary/20">
                        <button
                            type="button"
                            onClick={() => { setOpen(false); navigate("/coe/production/queue"); }}
                            className="w-full text-xs font-semibold text-primary hover:underline text-center"
                            data-testid="production-notification-view-all"
                        >
                            Open production queue
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
